import FW from '../fw/fw';
import Logger from '../fw/logger';
import MediaSessionMetadata from '../core/media-session-metadata';


export default class ApiDestroy {

  #rmp;
  #adsDestroyed = false;
  #postImaAdDestroyFn = null;
  #destroyRunning = true;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get running() {
    return this.#destroyRunning;
  }

  #fireDestroyCompleted() {
    this.#destroyRunning = false;
    this.#rmp.dispatch('destroycompleted');
    this.#rmp.resolvePromise('destroy');
  }

  #postImaAdDestroy() {
    if (this.#rmp.rmpAdsIma) {
      this.#rmp.rmpAdsIma.resetAdsForNewRequest();
      this.#rmp.rmpAdsIma.destroy();
    }
    this.#adsDestroyed = true;
  }

  #destroyAds() {
    Logger.print('api', 'start destroying ads');
    this.#rmp.rmpInitManagement.unwireInitAdEvents();
    // in case bad license key but ads: true, rmpAdsHelpers may not exist so check for it first
    if (this.#rmp.rmpAdsHelpers) {
      this.#rmp.rmpAdsHelpers.destroy();
    }
    this.#rmp.rmpPlaybackManagement.unwireAdAutoplayEvents();
    if (this.#rmp.adParser === 'ima') {
      this.#postImaAdDestroyFn = this.#postImaAdDestroy.bind(this);
      if (this.#rmp.adOnStage) {
        if (this.#rmp.adLinear) {
          this.#rmp.one('adcontentresumerequested', this.#postImaAdDestroyFn);
          this.#rmp.stopAds().catch(error => {
            this.#rmp.rmpUtils.warning(1021, error);
          });
        } else {
          this.#rmp.stopAds().catch(error => {
            this.#rmp.rmpUtils.warning(1021, error);
          });
          this.#postImaAdDestroyFn();
        }
      } else {
        this.#postImaAdDestroyFn();
      }
    } else {
      if (this.#rmp.adOnStage) {
        if (this.#rmp.adLinear) {
          this.#rmp.one('addestroyed', () => {
            this.#adsDestroyed = true;
          });
          this.#rmp.stopAds().catch(error => {
            this.#rmp.rmpUtils.warning(1021, error);
          });
        } else {
          this.#rmp.stopAds().catch(error => {
            this.#rmp.rmpUtils.warning(1021, error);
          });
          this.#adsDestroyed = true;
        }
      } else {
        this.#adsDestroyed = true;
      }
    }
  }

  #checkForPiP() {
    if (this.#rmp.rmpPip) {
      this.#rmp.rmpPip.destroy().then(() => {
        this.#fireDestroyCompleted();
      });
    } else {
      this.#fireDestroyCompleted();
    }
  }

  #destroyBuffer() {
    Logger.print('api', 'start destroying buffer');
    if (this.#rmp.readingShaka) {
      // if destroy called before shaka instance created we still need to 
      // go through _checkForPiP
      if (this.#rmp.shakaPlayer === null) {
        this.#checkForPiP();
        return;
      }
      if (this.#rmp.rmpShakaPlayer) {
        this.#rmp.rmpShakaPlayer.destroy();
      }
      // destroy with DRM does not fire each time with Shaka player
      // reproduce by blocking DRM server request in Chrome dev tool
      let runCallback = true;
      this.#rmp.shakaPlayer.destroy().then(() => {
        if (runCallback) {
          runCallback = false;
          this.#checkForPiP();
        }
      });
      window.setTimeout(() => {
        if (runCallback) {
          runCallback = false;
          this.#checkForPiP();
        }
      }, 1000);
    } else if (this.#rmp.readingHlsJS) {
      if (this.#rmp.hlsJS === null) {
        this.#checkForPiP();
        return;
      }
      if (this.#rmp.rmpHlsJS) {
        this.#rmp.rmpHlsJS.destroy();
      }
      this.#checkForPiP();
    } else if (this.#rmp.readingPDorNative) {
      FW.clearNativeHtml5Buffer(this.#rmp.dom.video);
      this.#checkForPiP();
    } else if (this.#rmp.readingOutstream) {
      this.#checkForPiP();
    }
  }

  #postAdsDestroy() {
    // captions destroy
    if (this.#rmp.rmpCCManagement) {
      this.#rmp.rmpCCManagement.destroy();
    }
    if (this.#rmp.rmpThreeSixty) {
      this.#rmp.rmpThreeSixty.destroy();
    }
    this.#destroyBuffer();
  }

  clear() {
    if (this.#rmp.rmpNativeHls) {
      this.#rmp.rmpNativeHls.destroy();
    }
    if (this.#rmp.rmpMediaSessionMetadata) {
      MediaSessionMetadata.destroy();
    }
    this.#rmp.rmpLicense.destroy();
    this.#rmp.rmpVolumeManagement.destroy();
    this.#rmp.rmpFullscreenManagement.destroy();
    this.#rmp.rmpSeekManagement.destroy();
    this.#rmp.rmpUIManagement.destroy();
    // destroy timeout/interval

    window.clearInterval(this.#rmp.analyticsInterval);

    if (this.#rmp.rmpAdsImaDai) {
      this.#rmp.rmpAdsImaDai.destroy();
    }
    // destroy viewable
    if (this.#rmp.rmpViewableObserver) {
      this.#rmp.rmpViewableObserver.destroy();
    }
    // destroy Cast
    if (this.#rmp.rmpGoogleCast) {
      this.#rmp.rmpGoogleCast.destroy();
    }
    // destroy Airplay
    if (this.#rmp.rmpAirPlay) {
      this.#rmp.rmpAirPlay.destroy();
    }
    // destroy thumbnails
    if (this.#rmp.rmpThumbnails) {
      this.#rmp.rmpThumbnails.destroy();
    }
    // destroy cue points
    if (this.#rmp.rmpCuePoints) {
      this.#rmp.rmpCuePoints.destroy();
    }
    // destroy ads
    if (this.#rmp.ads) {
      this.#destroyAds();
      const clearAdInterval = window.setInterval(() => {
        if (this.#adsDestroyed) {
          window.clearInterval(clearAdInterval);
          this.#postAdsDestroy();
        }
      }, 40);
    } else {
      this.#postAdsDestroy();
    }
  }

}
